import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderKanban, Plus, X, Loader2, Calendar, User as UserIcon, Search, Users, Mail, UserPlus, CheckCircle2, Trash2, Sparkles, BarChart3, ShieldCheck, Briefcase, Building2, UserCheck, UserX } from 'lucide-react';
import { setProject } from "../redux/authSlice"; 

export default function OrgAdminDashboard({ darkMode = true, initialTab = 'projects' }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.currentUser);
  
  const [activeTab, setActiveTab] = useState(initialTab || 'projects'); 
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data States
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  
  // Modal States
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState(''); 
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  const [projectData, setProjectData] = useState({ project_name: '', project_description: '' });
  const [inviteEmail, setInviteEmail] = useState('');

  // --- FETCH ALL DATA PARALLEL ---
  const fetchData = async () => {
    if (!user?.org_id) return;
    setLoading(true);
    try {
      const [projRes, teamRes, orgUsersRes] = await Promise.all([
        axios.get(`https://api.testoraglobal.in/api/project/org/${user.org_id}`),
        axios.get(`https://api.testoraglobal.in/api/user/org-team/${user.org_id}`),
        axios.get("https://api.testoraglobal.in/api/org/users")
      ]);
      
      if (projRes.data.success) setProjects(projRes.data.projects);
      if (teamRes.data.success) {
        setPendingInvites(teamRes.data.invites);
      }
      if (orgUsersRes.data.success) setTeamMembers(orgUsersRes.data.users);
    } catch (error) {
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [user?.org_id]);

  // --- FILTERING & SORTING FOR MODAL ---
  const filteredProjects = projects.filter(p => p.project_name?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredTeam = teamMembers.filter(m => m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.email?.toLowerCase().includes(searchQuery.toLowerCase()));
  
  const sortedModalUsers = [...teamMembers].sort((a, b) => a.full_name.localeCompare(b.full_name));
  
  const unassignedModalUsers = sortedModalUsers.filter(m => 
    !selectedProjectForAssign?.managers?.some(mgr => mgr.user_id === m.user_id)
  );

  const searchedModalUsers = unassignedModalUsers.filter(m => 
    m.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) || 
    m.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  // --- HANDLERS ---
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectData.project_name.trim()) return toast.error("Project name required");
    setCreating(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/project/create", { ...projectData, org_id: user.org_id });
      if (res.data.success) {
        toast.success("Project created!");
        setIsProjectModalOpen(false);
        setProjectData({ project_name: '', project_description: '' });
        fetchData();
      } else toast.error(res.data.message);
    } catch (err) { toast.error("Error creating project"); } 
    finally { setCreating(false); }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error("Email required");
    setCreating(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/user/invite", { email: inviteEmail, org_id: user.org_id });
      if (res.data.success) {
        toast.success("Invitation sent!");
        setIsInviteModalOpen(false);
        setInviteEmail('');
        fetchData();
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || "Error sending invite"); } 
    finally { setCreating(false); }
  };

  const handleToggleUserStatus = async (member) => {
    const nextIsActive = member.is_active === false;
    setUpdatingUserId(member.user_id);
    try {
      const res = await axios.put(
        `https://api.testoraglobal.in/api/org/users/${member.user_id}/status`,
        { is_active: nextIsActive }
      );
      if (res.data.success) {
        setTeamMembers((currentMembers) => currentMembers.map((currentMember) =>
          currentMember.user_id === member.user_id
            ? { ...currentMember, is_active: res.data.is_active }
            : currentMember
        ));
        toast.success(nextIsActive ? "User activated." : "User deactivated.");
      } else {
        toast.error(res.data.message || "Unable to update user status.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to update user status.");
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleDeleteUser = async (member) => {
    const displayName = member.full_name || member.email || "this user";
    if (!window.confirm(`Delete ${displayName}? This action cannot be undone.`)) return;

    setDeletingUserId(member.user_id);
    try {
      const res = await axios.delete(`https://api.testoraglobal.in/api/org/users/${member.user_id}`);
      if (res.data.success) {
        setTeamMembers((currentMembers) => currentMembers.filter(
          (currentMember) => currentMember.user_id !== member.user_id
        ));
        toast.success("User deleted.");
      } else {
        toast.error(res.data.message || "Unable to delete user.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete user.");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleAssignManager = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return toast.error("Please select a user to assign");
    
    setCreating(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/project/assign", { 
        project_id: selectedProjectForAssign.project_id,
        user_id: selectedUserId,
        role: "manager",
        org_id: user.org_id
      });
      if (res.data.success) {
        toast.success(res.data.message);
        const assignedUser = teamMembers.find(m => m.user_id === selectedUserId);
        setSelectedProjectForAssign(prev => ({
          ...prev, managers: [...(prev.managers || []), assignedUser]
        }));
        setSelectedUserId('');
        fetchData(); 
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || "Error assigning manager"); } 
    finally { setCreating(false); }
  };

  const handleRemoveManager = async (userId) => {
    setRemoving(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/project/unassign", { 
        project_id: selectedProjectForAssign.project_id,
        user_id: userId,
        role: "manager"
      });
      if (res.data.success) {
        toast.success("Manager removed");
        setSelectedProjectForAssign(prev => ({
          ...prev, managers: prev.managers.filter(m => m.user_id !== userId)
        }));
        fetchData();
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || "Error removing manager"); } 
    finally { setRemoving(false); }
  };

  const handleProjectClick = (project) => {
    dispatch(setProject(project));
    navigate(`/dashboard/${project.project_id}`);
  };

  const openAssignModal = (e, project) => {
    e.stopPropagation(); 
    setSelectedProjectForAssign(project);
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedUserId('');
    setUserSearchQuery('');
  };

  const cardClasses = darkMode ? "bg-[#0B1220] border border-gray-800 text-white" : "bg-white border border-slate-200 text-slate-900";
  const mutedText = darkMode ? "text-gray-400" : "text-slate-500";

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#020617] text-white' : 'bg-slate-50 text-slate-900'} p-6 lg:p-8`}>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className={`rounded-3xl border ${darkMode ? 'border-gray-800 bg-[#0B1220]' : 'border-slate-200 bg-white'} p-6 shadow-xl`}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-purple-500/10 px-3 py-1 text-sm font-semibold text-purple-400">
                <Building2 size={16} /> Workspace Admin
              </div>
              <h1 className="text-3xl font-bold tracking-tight">Organization command center</h1>
              <p className={`mt-2 text-sm ${mutedText}`}>
                Manage projects, oversee users, and review automation health from one place.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${darkMode ? 'border-gray-800 bg-[#111827]' : 'border-slate-200 bg-slate-100'}`}>
                <Search size={16} className="text-purple-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'users' ? 'users' : 'projects'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`bg-transparent text-sm outline-none ${darkMode ? 'text-white' : 'text-slate-800'}`}
                />
              </div>
              {activeTab === 'projects' ? (
                <button onClick={() => setIsProjectModalOpen(true)} className="flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 font-semibold text-white transition hover:bg-purple-500">
                  <Plus size={18} /> New Project
                </button>
              ) : (
                <button onClick={() => setIsInviteModalOpen(true)} className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 font-semibold text-white transition hover:bg-emerald-500">
                  <Mail size={18} /> Invite User
                </button>
              )}
            </div>
          </div>
        </div>

        {/* <div className={`rounded-2xl border ${darkMode ? 'border-gray-800 bg-[#0B1220]' : 'border-slate-200 bg-white'} p-2 shadow-sm`}>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveTab('projects')} className={`rounded-xl px-4 py-2.5 font-medium transition ${activeTab === 'projects' ? 'bg-purple-600 text-white' : darkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}><div className="flex items-center gap-2"><FolderKanban size={18}/> Projects</div></button>
            <button onClick={() => setActiveTab('users')} className={`rounded-xl px-4 py-2.5 font-medium transition ${activeTab === 'users' ? 'bg-purple-600 text-white' : darkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}><div className="flex items-center gap-2"><Users size={18}/> Users</div></button>
            <button onClick={() => navigate('/admin-dashboard/automation-intelligence')} className={`rounded-xl px-4 py-2.5 font-medium transition ${darkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}><div className="flex items-center gap-2"><Sparkles size={18}/> Automation Intelligence</div></button>
          </div>
        </div> */}

        {loading ? (
          <div className={`flex justify-center rounded-3xl border ${darkMode ? 'border-gray-800 bg-[#0B1220]' : 'border-slate-200 bg-white'} p-16`}><Loader2 size={40} className="animate-spin text-purple-500" /></div>
        ) : activeTab === 'projects' ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <AnimatePresence>
              {filteredProjects.map((project) => (
                <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} key={project.project_id} onClick={() => handleProjectClick(project)} className={`${cardClasses} hover:border-purple-500/50 rounded-2xl p-6 transition-all cursor-pointer group shadow-lg flex flex-col relative`}>
                  
                  {/* Assign Manager Button - Top Right */}
                  <button 
                    onClick={(e) => openAssignModal(e, project)} 
                    className="absolute top-4 right-4 p-2 bg-gray-800/60 hover:bg-purple-600 border border-gray-700 hover:border-purple-500 rounded-lg text-gray-300 hover:text-white transition shadow-sm z-10"
                    title="Manage Roles"
                  >
                    <UserPlus size={18} />
                  </button>

                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-500 mb-4 group-hover:bg-purple-500 group-hover:text-white transition-colors shrink-0"><FolderKanban size={24} /></div>
                  <h3 className="text-xl font-bold mb-2 pr-12 truncate group-hover:text-purple-400">{project.project_name}</h3>
                  <p className={`${mutedText} text-sm mb-6 line-clamp-2 flex-1`}>{project.project_description || "No description provided."}</p>
                  
                  <div className={`flex items-center justify-between text-xs ${mutedText} border-t ${darkMode ? 'border-gray-800/50' : 'border-slate-200'} pt-4 mt-auto`}>
                    <div className="flex items-center gap-1.5 truncate pr-2">
                      <UserIcon size={14} className="text-purple-500 shrink-0" />
                      <span className="truncate text-gray-300 font-medium">
                        {project.managers && project.managers.length > 0 ? project.managers.map(m => m.full_name).join(", ") : "Unassigned"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0"><Calendar size={14} />{new Date(project.created_date).toLocaleDateString()}</div>
                  </div>
                  <div className={`mt-3 flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${mutedText} ${darkMode ? 'border-gray-800 bg-[#070d17]' : 'border-slate-200 bg-slate-50'}`}>
                    <span>Advance AI calls this month</span>
                    <span className="font-semibold text-purple-400">{project.current_month_gemini_calls ?? 0}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="space-y-6">
            <div className={`rounded-3xl border ${darkMode ? 'border-gray-800 bg-[#0B1220]' : 'border-slate-200 bg-white'} p-6 shadow-sm`}>
              <div className="grid gap-4 md:grid-cols-3">
                <div className={`rounded-2xl border ${darkMode ? 'border-gray-800 bg-[#111827]' : 'border-slate-200 bg-slate-50'} p-4`}>
                  <p className={`text-sm ${mutedText}`}>Total users</p>
                  <p className="mt-2 text-2xl font-semibold">{teamMembers.length}</p>
                </div>
                <div className={`rounded-2xl border ${darkMode ? 'border-gray-800 bg-[#111827]' : 'border-slate-200 bg-slate-50'} p-4`}>
                  <p className={`text-sm ${mutedText}`}>Pending invites</p>
                  <p className="mt-2 text-2xl font-semibold">{pendingInvites.length}</p>
                </div>
                <div className={`rounded-2xl border ${darkMode ? 'border-gray-800 bg-[#111827]' : 'border-slate-200 bg-slate-50'} p-4`}>
                  <p className={`text-sm ${mutedText}`}>Projects</p>
                  <p className="mt-2 text-2xl font-semibold">{projects.length}</p>
                </div>
              </div>
            </div>
            {pendingInvites.length > 0 && (
              <div className={`border rounded-2xl p-6 shadow-lg ${darkMode ? 'border-yellow-600/30 bg-[#0B1220]' : 'border-amber-200 bg-amber-50'}`}>
                <h3 className="text-lg font-semibold text-yellow-500 mb-4">Pending Invitations ({pendingInvites.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingInvites.map(inv => (
                    <div key={inv.token} className="flex justify-between items-center bg-[#111827] p-3 rounded-lg border border-gray-800">
                      <span className="text-gray-300 text-sm truncate pr-2">{inv.email}</span>
                      <span className="text-[10px] uppercase font-bold tracking-wider bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded shrink-0">Expires: {new Date(inv.expires_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className={`${cardClasses} rounded-2xl p-6 shadow-lg`}>
              <h3 className="text-lg font-semibold mb-4">Team Members ({filteredTeam.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left">
                  <thead>
                    <tr className={`border-b text-xs uppercase tracking-wider ${darkMode ? 'border-gray-800 text-gray-500' : 'border-slate-200 text-slate-500'}`}>
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeam.map((member) => {
                      const displayName = member.full_name || member.user_name || member.email || "User";
                      const isActive = member.is_active !== false;
                      const isCurrentUser = member.email && member.email === user?.email;
                      const isUpdating = updatingUserId === member.user_id;
                      const isDeleting = deletingUserId === member.user_id;

                      return (
                        <tr key={member.user_id} className={`border-b last:border-b-0 ${darkMode ? 'border-gray-800/70' : 'border-slate-100'}`}>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-500/20 font-bold text-purple-400">
                                {displayName.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold">{displayName}</p>
                                <p className={`truncate text-xs ${mutedText}`}>{member.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${isActive ? 'bg-emerald-500/10 text-emerald-500' : darkMode ? 'bg-gray-700/60 text-gray-300' : 'bg-slate-200 text-slate-600'}`}>
                              {isActive ? "Active" : "Deactivated"}
                            </span>
                            {member.is_sso && (
                              <span className="ml-2 inline-flex rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-semibold text-blue-400">
                                SSO
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {!isCurrentUser && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleToggleUserStatus(member)}
                                  disabled={isUpdating || isDeleting}
                                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${isActive ? 'border-amber-500/30 text-amber-500 hover:bg-amber-500/10' : 'border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10'}`}
                                  aria-label={`${isActive ? "Deactivate" : "Activate"} ${displayName}`}
                                >
                                  {isUpdating ? <Loader2 size={14} className="animate-spin" /> : isActive ? <UserX size={14} /> : <UserCheck size={14} />}
                                  {isActive ? "Deactivate" : "Activate"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(member)}
                                  disabled={isUpdating || isDeleting}
                                  className="inline-flex items-center justify-center rounded-lg border border-red-500/30 p-2 text-red-500 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={`Delete ${displayName}`}
                                  title="Delete user"
                                >
                                  {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredTeam.length === 0 && (
                      <tr>
                        <td colSpan="3" className={`px-4 py-10 text-center text-sm ${mutedText}`}>
                          No team members found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ASSIGN MANAGER MODAL - TWO COLUMN LAYOUT */}
      <AnimatePresence>
        {isAssignModalOpen && (
           <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
           <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !creating && closeAssignModal()} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
           <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="relative bg-[#0B1220] border border-gray-700 shadow-2xl rounded-2xl w-full max-w-4xl overflow-hidden flex flex-col h-150 max-h-[90vh]">
             
             {/* Header */}
             <div className="flex justify-between items-center p-6 border-b border-gray-800 shrink-0">
                <h2 className="text-xl font-bold">Manage Roles</h2>
                <X size={24} className="cursor-pointer text-gray-400 hover:text-white transition" onClick={closeAssignModal}/>
             </div>

             <div className="p-6 flex-1 flex flex-col overflow-hidden">
               <p className="text-sm text-gray-400 mb-6 shrink-0">Manage assignments for <strong className="text-white">{selectedProjectForAssign?.project_name}</strong>.</p>
               
               {/* Two Column Grid */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 flex-1 overflow-hidden min-h-0">
                 
                 {/* LEFT COLUMN: Currently Assigned */}
                 <div className="flex flex-col bg-[#111827] rounded-xl border border-gray-800 p-4 overflow-hidden">
                    <h4 className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-4 shrink-0">Currently Assigned</h4>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 pr-2 space-y-2">
                      {selectedProjectForAssign?.managers?.length > 0 ? (
                        selectedProjectForAssign.managers.map(mgr => (
                          <div key={mgr.user_id} className="flex justify-between items-center bg-[#0B1220] p-3 rounded-lg border border-gray-800 transition-colors hover:border-gray-700">
                            <div className="flex items-center gap-3 overflow-hidden">
                               <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">{mgr.full_name.charAt(0)}</div>
                               <span className="text-sm font-medium text-white truncate">{mgr.full_name}</span>
                            </div>
                            <button 
                              onClick={() => handleRemoveManager(mgr.user_id)} 
                              disabled={removing}
                              className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded transition disabled:opacity-50 shrink-0"
                              title="Remove Manager"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-500">
                          <Users size={32} className="mb-2 opacity-20" />
                          <p className="text-sm">No managers assigned yet.</p>
                        </div>
                      )}
                    </div>
                 </div>

                 {/* RIGHT COLUMN: Assign New */}
                 <div className="flex flex-col overflow-hidden">
                    <h4 className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-4 shrink-0">Assign New Manager</h4>
                    
                    {/* Search Bar */}
                    <div className="relative mb-4 shrink-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                      <input
                        type="text" placeholder="Search by name or email..." value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="w-full bg-[#111827] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-purple-500 text-sm transition-colors"
                      />
                    </div>

                    <form onSubmit={handleAssignManager} className="flex flex-col flex-1 overflow-hidden min-h-0">
                      {/* Scrollable list of Unassigned users */}
                      <div className="overflow-y-auto space-y-2 mb-4 pr-2 custom-scrollbar flex-1">
                         {searchedModalUsers.length === 0 ? (
                            <p className="text-center text-gray-500 text-sm py-4 bg-[#111827] rounded-lg border border-gray-800">No available users found.</p>
                         ) : (
                           searchedModalUsers.map(member => (
                              <div 
                                key={member.user_id} 
                                onClick={() => setSelectedUserId(prev => prev === member.user_id ? '' : member.user_id)}
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition border ${selectedUserId === member.user_id ? 'bg-purple-600/20 border-purple-500' : 'bg-[#111827] border-gray-800 hover:border-gray-600'}`}
                              >
                                <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-xs shrink-0">
                                   {member.full_name.charAt(0)}
                                </div>
                                <div className="overflow-hidden flex-1">
                                   <p className="text-sm font-semibold truncate text-white">{member.full_name}</p>
                                   <p className="text-xs text-gray-500 truncate">{member.email}</p>
                                </div>
                                {selectedUserId === member.user_id && (
                                   <CheckCircle2 size={18} className="text-purple-500 shrink-0" />
                                )}
                              </div>
                           ))
                         )}
                      </div>

                      <div className="pt-4 border-t border-gray-800 shrink-0">
                         <button type="submit" disabled={creating || !selectedUserId} className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2">
                           {creating ? <Loader2 className="animate-spin" size={18}/> : "Assign Selected"}
                         </button>
                      </div>
                    </form>
                 </div>
               </div>

             </div>
           </motion.div>
         </div>
        )}
      </AnimatePresence>

      {/* PROJECT CREATION & INVITE MODALS */}
      <AnimatePresence>
        {isProjectModalOpen && (
           <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
           <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !creating && setIsProjectModalOpen(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
           <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="relative bg-[#0B1220] border border-gray-700 shadow-2xl rounded-2xl w-full max-w-lg p-6">
             <div className="flex justify-between items-center mb-6"><h2 className="text-xl font-bold">New Project</h2><X size={24} className="cursor-pointer text-gray-400 hover:text-white transition" onClick={() => setIsProjectModalOpen(false)}/></div>
             <form onSubmit={handleCreateProject}>
               <input type="text" name="project_name" value={projectData.project_name} onChange={(e) => setProjectData({...projectData, project_name: e.target.value})} placeholder="Project Name" className="w-full bg-[#111827] border border-gray-700 rounded-lg p-3 mb-4 outline-none focus:border-purple-500" />
               <textarea name="project_description" value={projectData.project_description} onChange={(e) => setProjectData({...projectData, project_description: e.target.value})} placeholder="Description" rows="3" className="w-full bg-[#111827] border border-gray-700 rounded-lg p-3 mb-6 outline-none focus:border-purple-500 resize-none" />
               <button type="submit" disabled={creating} className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-lg font-medium transition disabled:opacity-50">{creating ? <Loader2 className="animate-spin mx-auto"/> : "Create Project"}</button>
             </form>
           </motion.div>
         </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isInviteModalOpen && (
           <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
           <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !creating && setIsInviteModalOpen(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
           <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="relative bg-[#0B1220] border border-gray-700 shadow-2xl rounded-2xl w-full max-w-md p-6">
             <div className="flex justify-between items-center mb-6"><h2 className="text-xl font-bold">Invite Team Member</h2><X size={24} className="cursor-pointer text-gray-400 hover:text-white transition" onClick={() => setIsInviteModalOpen(false)}/></div>
             <form onSubmit={handleInviteUser}>
               <label className="block text-sm text-gray-400 mb-2">Member's Email Address</label>
               <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" className="w-full bg-[#111827] border border-gray-700 rounded-lg p-3 mb-6 outline-none focus:border-green-500" />
               <button type="submit" disabled={creating} className="w-full bg-green-600 hover:bg-green-500 text-white p-3 rounded-lg font-medium transition disabled:opacity-50">{creating ? <Loader2 className="animate-spin mx-auto"/> : "Send Invitation"}</button>
             </form>
           </motion.div>
         </div>
        )}
      </AnimatePresence>
    </div>
  );
}